import React, { ReactNode, useState, useEffect, useRef } from 'react';
import { Box, Button, Card, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import FmtpTable from 'src/components/common/FmtpTable/FmtpTable';
import { CellContext, ColumnDef } from '@tanstack/react-table';
import { SnackbarKey, useSnackbar } from 'notistack';
import { FmtpCustomer, FmtpError, FmtpErrorCode } from '@lanterds/types';
import './ManageCustomers.scss';
import { getCustomers } from 'src/api/customer';
import EditCustomerDialog from './EditCustomerDialog';
import { CustomerForm } from './types';

export default function ManageCustomers() {
    const [createCustomerDialogOpen, setCreateCustomerDialogOpen] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<CustomerForm | null>(null);
    const [isSavingCustomer, setIsSavingCustomer] = useState(false);
    const [customers, setCustomers] = useState<FmtpCustomer[] | null>(null);
    const [isFetchingCustomers, setIsFetchingCustomers] = useState(false);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const snackbarRef = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        return () => {
            if (snackbarRef.current) {
                closeSnackbar(snackbarRef.current);
                snackbarRef.current = null;
            }
        };
    }, [closeSnackbar]);

    useEffect(() => {
        if (!customers && !isFetchingCustomers) {
            getCustomerList();
        }
    }, [customers, isFetchingCustomers, isSavingCustomer]);

    const getCustomerList = async () => {
        snackbarRef.current = enqueueSnackbar('Getting customers...', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        setIsFetchingCustomers(true);

        getCustomers()
            .then((data) => {
                setCustomers(
                    data.sort((a: FmtpCustomer, b: FmtpCustomer) =>
                        a.name.toLocaleLowerCase().localeCompare(b.name.toLocaleLowerCase()),
                    ),
                );
            })
            .catch((err) => {
                const message = 'An error occurred while getting customer list';
                const errorCode: FmtpErrorCode = 'SHU017';
                console.error(err);
                throw new FmtpError({
                    message,
                    errorCode,
                });
            })
            .finally(() => {
                if (snackbarRef.current) {
                    closeSnackbar(snackbarRef.current);
                    snackbarRef.current = null;
                }
                setIsFetchingCustomers(false);
            });
    };

    const handleOpenCreateUserDialog = () => {
        setCreateCustomerDialogOpen(true);
    };

    const handleCloseCreateCustomerDialog = () => {
        setCreateCustomerDialogOpen(false);
        setSelectedCustomer(null);
    };

    const onCustomerClick = (customer: CustomerForm) => {
        setSelectedCustomer(customer);
        setCreateCustomerDialogOpen(true);
    };

    const handleCustomerCreated = async () => {
        handleCloseCreateCustomerDialog();
        closeSnackbar();
        getCustomerList();
    };

    const columns: ColumnDef<FmtpCustomer>[] = [
        {
            accessorKey: 'name',
            header: 'Name',
            meta: {
                innerHtml: ({ row, getValue }: CellContext<FmtpCustomer, unknown>) => {
                    return (
                        <Typography
                            onClick={() =>
                                onCustomerClick({
                                    ...row.original,
                                    ...(row.original.d365Id ? { d365Id: row.original.d365Id[0] } : {}),
                                } as CustomerForm)
                            }
                            className="name-cell-text"
                        >
                            {getValue() as ReactNode}
                        </Typography>
                    );
                },
            },
        },
        {
            accessorKey: 'fmtpId',
            header: 'FMTP ID',
        },
        {
            accessorKey: 'lt1CustomerCode',
            header: 'LT1 Customer Code',
        },
        {
            accessorKey: 'lt1Id',
            header: 'LT1 ID',
            filterFn: 'includesString',
        },
        {
            accessorKey: 'd365Id',
            header: 'D365 ID',
            filterFn: 'includesString',
        },
    ];

    return (
        <Box id="manage-customers-container" className="auto-fill-height">
            <Card id="manage-customers-card" className="auto-fill-height">
                <Grid container spacing={2}>
                    <Grid xs={12} className="manage-customers-header">
                        <Typography variant="h5">Customers</Typography>
                    </Grid>
                    <Grid xs={12} container className="user-buttons-grid">
                        <Grid>
                            <Button variant="contained" size="medium" onClick={handleOpenCreateUserDialog}>
                                Create New Customer
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <FmtpTable
                    data={customers ?? []}
                    columns={columns}
                    enableSorting={true}
                    isResizable={true}
                    enableFilters={true}
                    isFetching={isFetchingCustomers || (!isFetchingCustomers && !customers)}
                    showTable={!isFetchingCustomers && !!customers}
                />
            </Card>
            <EditCustomerDialog
                open={createCustomerDialogOpen}
                onClose={handleCloseCreateCustomerDialog}
                onCustomerCreated={handleCustomerCreated}
                customerInfo={selectedCustomer}
                isEditing={!!selectedCustomer}
                setIsSavingCustomer={setIsSavingCustomer}
            />
        </Box>
    );
}
